import { mediaAPI } from '@/providers/apis/media/media';
import { Vue, Component, Provide, Prop } from 'vue-property-decorator';
import RegistryFormFields from '../registry-form-field';
import VueMultiSelect from 'vue-multiselect';
import { PERMISSION_CHECK } from '@/utils/constants';
import { sessionService } from '@/providers/services/auth/session';
import { isEmptyValue } from '@/utils/utils';

@Component({
    name: 'create-registry-form',
    components: {
        'field-render': () => import('@/components/learing-registry/field-render/field-render.vue'),
        'vue-multi-select': VueMultiSelect,
    },
})

export default class CreateRegistryForm extends Vue {

    private get formSections() {
       return this.createdFormList();
    }

    private get isEditView() {
        const isEditView = this.isPublisher ? this.activeToolData.toolData : this.activeToolData;
        return !!isEditView;
    }

    @Provide('validator') public $validator: any = this.$validator;

    /**
     * Help to render the grades list in html
     */
    private gradesList = [];

    // ----------------------------------------------------------------------------
    // Properties

    private formInstance = new RegistryFormFields();

    private submitted: boolean = false;

    @Prop()
    private toolPartners: any;

    @Prop()
    private activeToolData: any;

    private categoryInfo: any = [];

    @Prop()
    private publisherTools: any;

    @Prop()
    private isPublisher?: boolean;

    private selectedTool: any = null;

    private permissonCheck = PERMISSION_CHECK;

    private isLoading: boolean = false;

    /**
     * Initiate default value in toolData objects
     */
    private toolData: any = {
        defaultLaunchContainer: 'Embed',
        protocal: 'LTI',
        toolCategory: 'Curriculum',
        publicKeyType: 'RSA',
        LTIVersion: '1.1',
    };

    private get isSuperAdmin() {
        return sessionService.isSuperAdminUser();
    }

    private toolInfo = {
        0: {
            item: ['name', 'thumbnail'],
            className: 'two-column',
        },
        1: {
            item: ['description'],
            className: 'one-column',
        },
        2: {
            item: ['partner'],
            className: 'one-column',
        },
    };

    private otherMetda = {
        0: {
            item: ['defaultLaunchContainer', 'toolCategory', 'audience', 'rating'],
            className: 'two-column',
        },
        1: {
            item: ['customParams'],
            className: 'one-column',
        },
    };

    private toolConfig = {
        0: {
            item: ['toolType', 'toolURL'],
            className: 'one-column',
        },
        1: {
            item: ['clientId', 'LTIVersion', 'publicKeyType', 'publicKey', 'username', 'clientCode', 'imageURLs', 'requestMethod', 'keysetURL'],
            className: 'two-column',
        },
        2: {
            item: ['initateLoginURL', 'redirectURI'],
            className: 'one-column',
        },
    };

    private toolCategories = {
        0: {
            item: ['category', 'subject'],
            className: 'two-column',
        },
        1: {
            item: ['grades'],
            className: 'one-column',
        },
    };

    // ---------------------------------------------------------------------------
    // Hooks

    private mounted() {
        this.loadData();
        this.loadClassificationSubjects();
    }


    // -----------------------------------------------------------------------------
    // Actions

    private onAdd() {
        if (!this.isSuperAdmin && this.isEditView) {
            return;
        }
        this.submitted = true;
        this.$validator.validateAll().then((valid: any) => {
            if (valid) {
                this.isLoading = true;
                this.uploadThumbnail(this.toolData).then((fileName) => {
                    this.$emit('onAddTool', this.toolData);
                    setTimeout(() => {
                        this.isLoading = false;
                    }, 1000);
                });
            }
        });
    }

    private onAddCategory() {
        const toolData = this.toolData;
        const categoryItem = this.formInstance.selectedCategory(toolData.category, toolData.subject);
        this.parseCategoryList(categoryItem);
        this.toolData.categoryInfo = this.categoryInfo;
        this.formInstance.onReset();
    }

    private onSelectPublisherTool(toolData: any) {
        this.toolData = Object.assign({}, toolData);
        this.toolData.name = null;
        this.toolData.clientId = null;
        this.formInstance.activeToolType = toolData.toolType;
        this.formInstance.activePublicKeyType = toolData.publicKeyType;
    }

    private onClearGrade(grade: any) {
        const categoryInfo = this.categoryInfo || [];
        const activeCategory = categoryInfo.find((category: any) => category.id === grade.category.id);
        const activeSubject = activeCategory && activeCategory.subjects.find(
                (subject: any) => subject.id === grade.subject.id);
        const gradeFilter = activeSubject ? activeSubject.grades.filter((item: any) => item.id !== grade.id) : [];
        activeSubject.grades = gradeFilter;
        this.parseGradesList();
    }

    // --------------------------------------------------------------------------
    // Methods

    private setPartnersList() {
        const toolDetails = this.formInstance.toolDetails;
        const propBind = toolDetails.partner.propBind;
        propBind.options = this.toolPartners;
    }


    private uploadThumbnail(payload: any) {
        if (payload.thumbnail instanceof File) {
            return mediaAPI.uploadFile(payload.thumbnail, 'image').then((fileData) => {
                return this.$set(this.toolData, 'thumbnail', fileData.filename);
            });
        }
        return Promise.resolve(payload.thumbnail);
    }

    private loadData() {
        this.setPartnersList();
        if (this.activeToolData && (!this.isPublisher || this.activeToolData.toolData)) {
            const toolData = this.isPublisher ? this.activeToolData.toolData : this.activeToolData;
            this.toolData = toolData;
            this.formInstance.activeToolType = toolData.toolType;
            this.formInstance.activePublicKeyType = toolData.publicKeyType;
        }
    }


    // Help to push grades - subjects under the categories
    private parseCategoryList(categoryItem: any) {
        const categoryInfo = this.categoryInfo || [];
        const { category, subject } = categoryItem;
        const selectedCategory = Object.assign({}, category);
        let activeCategory = categoryInfo.find((item: any) => item.id === category.id);
        if (!activeCategory) {
            activeCategory = selectedCategory;
            this.categoryInfo.push(activeCategory);
        }
        const subjectItem = {
            id: subject.id,
            title: subject.title,
        };
        activeCategory.subjects = activeCategory.subjects || [];
        let activeSubject = activeCategory.subjects.find((item: any) => item.id === subjectItem.id);
        if (!activeSubject) {
            activeSubject = subjectItem;
            activeCategory.subjects.push(activeSubject);
        }
        activeSubject.grades = activeSubject.grades || [];
        this.$set(activeSubject, 'grades', [...activeSubject.grades , ...this.toolData.grades]);
        this.$set(this.toolData, 'category', null);
        this.$set(this.toolData, 'subject', null);
        this.$set(this.toolData, 'grades', null);
        this.parseGradesList();
    }

    private parseGradesList() {
        const categoryInfo = this.categoryInfo || [];
        const gradeList: any = [];
        categoryInfo.forEach((category: any) => {
            category.subjects.forEach((subject: any) => {
                subject.grades.forEach((grade: any) => {

                    const gradeObj = Object.assign({}, {category} , {subject}, grade);
                    gradeList.push(gradeObj);
                });
            });
        });

        this.gradesList = gradeList;
    }

    private createdFormList() {
        const toolDetails = this.formInstance.toolDetails;
        const propBind = toolDetails.partner.propBind;
        const toolConfig = this.formInstance.toolConfig;
        propBind.options = this.toolPartners;
        if (this.isEditView) {
            toolDetails.partner.isDisabled = true;
        }
        if (!this.$access.isHavingPermisson(this.permissonCheck.createTool) && toolConfig) {
            toolConfig.toolType.isDisabled = true;
        }
        let formSection = [
            {
                name: 'Tool Details',
                formData: toolDetails,
            },
            {
                name: 'Tool Categories',
                formData: this.formInstance.categoryInfo,
                isCategoryInfo: true,
                className: 'tool-category',
            },
            {
                name: 'Other Metadata',
                formData: this.formInstance.otherMetadata,
            },
            {
                name: 'Tool Config',
                formData: this.formInstance.toolConfig,
                isTenantTool: true,
                className: 'tool-config',
            },
        ];
        if (this.isPublisher) {
            formSection = formSection.filter((item) => !item.isCategoryInfo);
        }
        if (!this.$access.isHavingPermisson(this.permissonCheck.createTool)) {
            formSection = formSection.filter((item) => item.isTenantTool);
        }
        return formSection;
    }

    private loadClassificationSubjects() {
        const toolData = this.isPublisher ? this.activeToolData.toolData : this.activeToolData;
        if (toolData) {
            const {subject, category} = toolData;
            if (category) {
                this.formInstance.getSubjectList(category);
            }
            if (subject) {
                this.formInstance.onChangeSubject(subject);
            }
        }
    }

}
